"use client";

import type { ConsentChoices } from "./types";

const ANONYMOUS_ID_KEY = "ld_consent_session_id";

export type ConsentEventName =
	| "consent_banner_shown"
	| "consent_accepted"
	| "consent_rejected"
	| "consent_categories_changed";

export type ConsentEventProperties = {
	choices?: ConsentChoices;
	surface?: "banner" | "preferences_modal";
	trigger?: "accept_all" | "reject_all" | "save_preferences" | "auto_open";
	locale?: string;
	// Host-only external referrer of the page the banner appeared on, or
	// "(direct)" / "(internal)". Never the full URL (privacy, search queries).
	entry_referrer_host?: string;
	entry_utm_source?: string | null;
};

/**
 * Where this visitor came from, for `consent_banner_shown`. The server-side
 * consent relay only sees our own page as `Referer`, so the external referrer
 * must be read from the browser. Host only, never the full URL.
 */
export function entryAttribution(): {
	entry_referrer_host: string;
	entry_utm_source: string | null;
} {
	let host = "(direct)";
	let utm: string | null = null;
	try {
		if (document.referrer) {
			const refHost = new URL(document.referrer).host;
			host = refHost === window.location.host ? "(internal)" : refHost;
		}
		utm = new URLSearchParams(window.location.search).get("utm_source");
	} catch {
		// best effort — attribution must never break the consent UI
	}
	return { entry_referrer_host: host, entry_utm_source: utm };
}

// `localStorage`, not `sessionStorage`: a session-scoped id mints a NEW
// anonymousId every tab/session, which is exactly what fanned out to 8,515
// distinct PostHog persons/7d against 857 real `$pageview` persons
// (2026-08-04 product-signal audit). One browser should mint ONE id across
// its lifetime — `localStorage` persists across sessions/tabs the same
// origin shares.
function getAnonymousId(): string {
	if (typeof window === "undefined") return "anon_ssr";
	try {
		const existing = window.localStorage.getItem(ANONYMOUS_ID_KEY);
		if (existing) return existing;
		const id = `anon_${
			typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
				? crypto.randomUUID()
				: Math.random().toString(36).slice(2)
		}`;
		window.localStorage.setItem(ANONYMOUS_ID_KEY, id);
		return id;
	} catch {
		return `anon_${Math.random().toString(36).slice(2)}`;
	}
}

/**
 * Same guard `postHogBeforeSend` applies to every non-`$exception` client
 * event (`src/lib/analytics/posthog-before-send.ts`) — but this beacon posts to
 * a FIRST-PARTY route (`/api/consent/log`) that never passes through
 * posthog-js, so it was the one client emitter automation could still reach.
 *
 * Measured 2026-08-27 that gap dominated the event stream: `consent_banner_shown`
 * ran 47,885 events / 45,589 distinct ids in 30 days against 2,951 real browser
 * people, 10,040 of the last 7 days' 11,700 events came from US datacenter IPs
 * with a fresh context per request (1.004 events per id — localStorage never
 * persisted), and Indonesia — the actual audience — accounted for 314. The
 * banner impression was measuring headless browsers, not consent behaviour, and
 * was on track to spend a third of the free event tier doing it.
 */
function isAutomatedBrowser(): boolean {
	return typeof navigator !== "undefined" && navigator.webdriver === true;
}

export function trackConsentEvent(
	event: ConsentEventName,
	properties: ConsentEventProperties = {},
): void {
	if (typeof window === "undefined") return;
	if (isAutomatedBrowser()) return;
	const body = JSON.stringify({
		event,
		properties,
		anonymousId: getAnonymousId(),
		path: window.location.pathname,
	});
	try {
		// keepalive lets the request survive a page-unload triggered by the
		// banner action (e.g. user navigating away immediately after accepting).
		void fetch("/api/consent/log", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body,
			keepalive: true,
		}).catch(() => undefined);
	} catch {
		// best effort — never throw from a consent UI handler
	}
}
